import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, BookOpen, Award, FileText, Clock, Settings, LogOut, Loader2, Save, Eye, EyeOff, Globe, Download, ShoppingCart, ArrowRight, MapPin, Monitor, Video } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { useToast } from '@/hooks/use-toast';
import { eligibleTrainingsData } from '@/data/eligibleTrainings';
import logo from '@/assets/gaius-logo.webp';

interface Enrollment {
  id: string;
  training_slug: string;
  training_title: string;
  modality: string;
  selected_webinars: { index: number; title: string }[] | null;
  total_price: string;
  created_at: string;
}

const EnrolledTrainings: React.FC<{ fr: boolean; userId: string }> = ({ fr, userId }) => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('enrollments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (data) setEnrollments(data as unknown as Enrollment[]);
      setLoading(false);
    };
    fetch();
  }, [userId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (enrollments.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className="mb-4">{fr ? 'Aucune formation en cours.' : 'No active trainings.'}</p>
        <Button variant="outline" onClick={() => {
          const el = document.querySelector('[data-value="catalog"]') as HTMLElement;
          el?.click();
        }}>
          {fr ? 'Voir le catalogue' : 'Browse catalog'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {enrollments.map((e) => (
        <div key={e.id} className="p-4 rounded-xl border border-border bg-card space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-foreground">{e.training_title}</h4>
              <div className="flex items-center gap-2 mt-1">
                {e.modality === 'onsite' ? (
                  <Badge variant="outline" className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />{fr ? 'Présentiel' : 'On-site'}</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs flex items-center gap-1"><Monitor className="w-3 h-3" />{fr ? 'Distanciel' : 'Remote'}</Badge>
                )}
                <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString(fr ? 'fr-FR' : 'en-US')}</span>
              </div>
            </div>
            <span className="font-bold text-foreground">{e.total_price}</span>
          </div>
          {e.modality === 'remote' && e.selected_webinars && (
            <div className="space-y-1 pl-2">
              {e.selected_webinars.map((w) => (
                <div key={w.index} className="flex items-center gap-2 text-sm text-muted-foreground border-l-2 border-primary/30 pl-2">
                  <Video className="w-3 h-3 text-primary" />
                  {fr ? `Webinaire ${w.index}/5` : `Webinar ${w.index}/5`} : {w.title}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

interface Profile {
  full_name: string | null;
  company: string | null;
  avatar_url: string | null;
}

// Build catalog from eligible trainings data
const getTrainingCatalog = (fr: boolean) => {
  const data = fr ? eligibleTrainingsData.fr : eligibleTrainingsData.en;
  return Object.values(data).map((t: any) => ({
    id: t.slug,
    title: t.title,
    subtitle: t.subtitle,
    duration: t.durationOnsite,
    badge: fr ? 'OPCO · FIF-PL · CPF' : 'OPCO · FIF-PL · CPF',
    price: t.priceOnsite,
    priceRemote: t.priceRemote,
    funding: fr ? 'Éligible OPCO, FIF-PL, CPF' : 'Eligible OPCO, FIF-PL, CPF',
    description: t.subtitle,
  }));
};

export const DashboardPage = () => {
  const { language, setLanguage } = useLanguage();
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Account edit state
  const [editName, setEditName] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const fr = language === 'fr';

  useEffect(() => {
    if (!user && !loading) navigate(fr ? '/connexion' : '/auth');
  }, [user, loading, navigate, fr]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('full_name, company, avatar_url')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        setProfile(data);
        setEditName(data.full_name || '');
        setEditCompany(data.company || '');
      }
      setLoadingProfile(false);
    };
    if (user) fetchProfile();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: editName.trim(), company: editCompany.trim() })
      .eq('user_id', user.id);
    setSavingProfile(false);
    if (error) {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: error.message });
    } else {
      setProfile(p => p ? { ...p, full_name: editName.trim(), company: editCompany.trim() } : p);
      toast({ title: fr ? 'Profil mis à jour' : 'Profile updated' });
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: fr ? 'Veuillez saisir votre mot de passe actuel' : 'Please enter your current password' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: fr ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: fr ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match' });
      return;
    }
    setSavingPassword(true);
    
    // Re-authenticate by verifying the current password first
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user!.email!,
      password: currentPassword,
    });
    
    if (verifyError) {
      setSavingPassword(false);
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: fr ? 'Mot de passe actuel incorrect' : 'Current password is incorrect' });
      return;
    }
    
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSavingPassword(false);
    if (error) {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: fr ? 'Erreur lors de la mise à jour du mot de passe' : 'Error updating password' });
    } else {
      toast({ title: fr ? 'Mot de passe mis à jour' : 'Password updated' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    }
  };

  if (loading || loadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  const displayName = profile?.full_name || user.email?.split('@')[0] || 'Utilisateur';
  const catalog = getTrainingCatalog(fr);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={fr ? "Votre espace Gaius" : "Your Gaius Space"}
        description={fr ? "Votre espace personnel de formation IA juridique" : "Your personal legal AI training space"}
      />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={logo} alt="Gaius" className="h-10" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-foreground">{fr ? 'Votre espace Gaius' : 'Your Gaius Space'}</h1>
                <p className="text-sm text-muted-foreground">
                  {fr ? 'Bienvenue' : 'Welcome'}, {displayName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="outline" size="sm">{fr ? 'Accueil' : 'Home'}</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-muted-foreground">
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{fr ? 'Déconnexion' : 'Logout'}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="catalog" className="w-full">
          <TabsList className="grid w-full grid-cols-5 lg:w-[700px] mb-8">
            <TabsTrigger value="catalog" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden sm:inline">{fr ? 'Catalogue' : 'Catalog'}</span>
            </TabsTrigger>
            <TabsTrigger value="trainings" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">{fr ? 'Mes formations' : 'My Trainings'}</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">{fr ? 'Documents' : 'Documents'}</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">{fr ? 'Historique' : 'History'}</span>
            </TabsTrigger>
            <TabsTrigger value="account" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">{fr ? 'Mon compte' : 'Account'}</span>
            </TabsTrigger>
          </TabsList>

          {/* ========== CATALOGUE ========== */}
          <TabsContent value="catalog">
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">{fr ? 'Catalogue des formations' : 'Training Catalog'}</h2>
                <p className="text-sm text-muted-foreground">{fr ? 'Inscrivez-vous à nos programmes de formation IA juridique' : 'Enroll in our legal AI training programs'}</p>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {catalog.map((training) => (
                  <Card key={training.id} className="border-border flex flex-col opacity-50 cursor-not-allowed relative">
                    <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground text-[10px] px-2 py-0.5 shadow-md z-10">
                      {fr ? 'Bientôt disponible' : 'Soon'}
                    </Badge>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg">{training.title}</CardTitle>
                      </div>
                      <Badge variant="secondary" className="w-fit text-xs">{training.badge}</Badge>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <p className="text-sm text-muted-foreground mb-4 flex-1">{training.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{fr ? 'Durée' : 'Duration'}</span>
                          <span className="font-medium text-foreground">{training.duration}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{fr ? 'Présentiel' : 'On-site'}</span>
                          <span className="font-medium text-foreground">{training.price}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{fr ? 'Distanciel' : 'Remote'}</span>
                          <span className="font-medium text-foreground">{training.priceRemote}</span>
                        </div>
                        <p className="text-xs text-primary">{training.funding}</p>
                        <div className="flex gap-2 mt-2">
                          <Button className="flex-1 pointer-events-none" disabled>
                            {fr ? 'S\'inscrire' : 'Enroll'}
                          </Button>
                          <Button variant="outline" size="icon" disabled className="pointer-events-none">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ========== MES FORMATIONS ========== */}
          <TabsContent value="trainings">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {fr ? 'Mes formations' : 'My Trainings'}
                </CardTitle>
                <CardDescription>
                  {fr ? 'Accédez à vos webinaires et modules en cours' : 'Access your webinars and ongoing modules'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EnrolledTrainings fr={fr} userId={user.id} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== DOCUMENTS / ATTESTATIONS ========== */}
          <TabsContent value="documents">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  {fr ? 'Documents & Attestations' : 'Documents & Certificates'}
                </CardTitle>
                <CardDescription>
                  {fr ? 'Téléchargez vos attestations de formation (formats OPCO, FIF-PL, CPF)' : 'Download your training certificates (OPCO, FIF-PL, CPF formats)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Download className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{fr ? 'Aucun document disponible. Complétez une formation pour obtenir vos attestations.' : 'No documents available. Complete a training to get your certificates.'}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== HISTORIQUE ========== */}
          <TabsContent value="history">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  {fr ? 'Historique des formations' : 'Training History'}
                </CardTitle>
                <CardDescription>
                  {fr ? 'Récapitulatif de tous les webinaires et modules complétés' : 'Summary of all completed webinars and modules'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{fr ? 'Aucun historique pour le moment.' : 'No history yet.'}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== MON COMPTE ========== */}
          <TabsContent value="account">
            <div className="space-y-6">
              {/* Personal info */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    {fr ? 'Informations personnelles' : 'Personal Information'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{fr ? 'Nom complet' : 'Full Name'}</Label>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input value={user.email || ''} disabled className="bg-muted" />
                    </div>
                    <div className="space-y-2">
                      <Label>{fr ? 'Cabinet / Organisation' : 'Firm / Organization'}</Label>
                      <Input value={editCompany} onChange={(e) => setEditCompany(e.target.value)} />
                    </div>
                  </div>
                  <Button onClick={handleSaveProfile} disabled={savingProfile}>
                    {savingProfile && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <Save className="w-4 h-4 mr-2" />
                    {fr ? 'Enregistrer' : 'Save'}
                  </Button>
                </CardContent>
              </Card>

              {/* Language */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    {fr ? 'Langue' : 'Language'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button variant={language === 'fr' ? 'default' : 'outline'} onClick={() => setLanguage('fr')}>
                      🇫🇷 Français
                    </Button>
                    <Button variant={language === 'en' ? 'default' : 'outline'} onClick={() => setLanguage('en')}>
                      🇬🇧 English
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Password */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-primary" />
                    {fr ? 'Changer le mot de passe' : 'Change Password'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{fr ? 'Nouveau mot de passe' : 'New password'}</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{fr ? 'Confirmer' : 'Confirm'}</Label>
                      <Input
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  <Button onClick={handleChangePassword} disabled={savingPassword}>
                    {savingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {fr ? 'Mettre à jour le mot de passe' : 'Update password'}
                  </Button>
                </CardContent>
              </Card>

              {/* Quitter Gaius */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LogOut className="w-5 h-5 text-muted-foreground" />
                    {fr ? 'Quitter Gaius' : 'Leave Gaius'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Button variant="outline" onClick={handleSignOut} className="w-full sm:w-auto">
                      <LogOut className="w-4 h-4 mr-2" />
                      {fr ? 'Se déconnecter' : 'Log out'}
                    </Button>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-3">
                      {fr ? 'Supprimer définitivement votre compte et toutes vos données.' : 'Permanently delete your account and all your data.'}
                    </p>
                    <DeleteAccountDialog onAccountDeleted={() => navigate('/compte-supprime')} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
